// src/Blog.js
import React, { useEffect, useMemo, useState } from "react";
import { BLOG_POSTS, findPostBySlug, SUPPORTED_LANGS } from "./blogPosts";
import { tUI } from "./i18n";
import ReactMarkdown from "react-markdown";

const FALLBACK_LANG = "en";

function normalizeLang(lang) {
  return SUPPORTED_LANGS.includes(lang) ? lang : FALLBACK_LANG;
}

export default function Blog({ language }) {
  // Language coming from the URL (en, fr, de, it, es)
  const [urlLang, setUrlLang] = useState(normalizeLang(language));

  // Raw slug taken from the URL (/xx/blog/<slug>/)
  const [activeSlug, setActiveSlug] = useState(null);

  // Use URL language everywhere below
  const lang = urlLang;

  // ---------- INTERNAL ARTICLE LINK HANDLER ----------
  // Intercept clicks on internal /xx/blog/... links so we stay in SPA mode
  const handleArticleLinkClick = (href, event) => {
    if (!href) return;

    // Only handle root-relative links: /en/blog/..., /fr/blog/..., /blog/...
    if (!href.startsWith("/")) return;

    // /en/blog/slug or /fr/blog/slug or /blog/slug
    const match = href.match(/^\/(?:(en|fr|de|it|es)\/)?blog\/([^/]+)\/?$/);
    if (!match) {
      // Not an internal blog link → let browser handle it
      return;
    }

    event.preventDefault();

    const hrefLang = normalizeLang(match[1] || lang);
    const slugPart = match[2];

    // Find the post by slug in ANY language (more robust)
    const post =
      BLOG_POSTS.find(
        (p) => p.slug && Object.values(p.slug).includes(slugPart)
      ) || null;

    if (!post) {
      // Unknown slug → do nothing (or you could fallback to browser)
      return;
    }

    const finalLang = hrefLang;
    const finalSlug = post.slug[finalLang] || post.slug.en;

    // Update state directly (no popstate)
    setUrlLang(finalLang);
    setActiveSlug(finalSlug);

    // Keep URL in sync for SEO + reload
    const newPath = `/${finalLang}/blog/${finalSlug}`;
    window.history.pushState({}, "", newPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---------- Sync with URL on first load + back/forward ----------
  useEffect(() => {
    const syncFromLocation = () => {
      const path = window.location.pathname;
      const match = path.match(/^\/(en|fr|de|it|es)\/blog(?:\/([^/]+))?\/?$/);

      if (match) {
        const pathLang = normalizeLang(match[1]); // en, fr, de, it, es
        setUrlLang(pathLang); // update URL language

        if (match[2]) {
          // detail page: /xx/blog/slug
          setActiveSlug(match[2]);
        } else {
          // blog home: /xx/blog
          setActiveSlug(null);
        }
      } else {
        // Not a blog URL → just reset selection
        setActiveSlug(null);
      }
    };

    syncFromLocation();
    window.addEventListener("popstate", syncFromLocation);
    return () => window.removeEventListener("popstate", syncFromLocation);
  }, []); // IMPORTANT: no [language] here

  // ---------- When global language changes while on blog ----------
  useEffect(() => {
    const targetLang = normalizeLang(language);
    const path = window.location.pathname;
    const match = path.match(/^\/(en|fr|de|it|es)\/blog(?:\/([^/]+))?\/?$/);

    if (!match) {
      // Not on a blog URL → just keep URL lang in sync for later
      setUrlLang(targetLang);
      return;
    }

    const currentUrlLang = normalizeLang(match[1]);

    if (currentUrlLang === targetLang) {
      // URL already has right lang
      return;
    }

    // 👉 User is on the blog and changed language in navbar:
    // send them to the BLOG HOME in the new language
    const newPath = `/${targetLang}/blog`;
    window.history.pushState({}, "", newPath);

    // let the first effect re-sync activeSlug + urlLang
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [language]);

  // ---------- Which post is currently open? ----------
  const activePost = useMemo(() => {
    if (!activeSlug) return null;

    // 1) Try in current language
    let post = findPostBySlug(lang, activeSlug);
    if (post) return post;

    // 2) Fallback: find by slug in ANY language
    post =
      BLOG_POSTS.find(
        (p) => p.slug && Object.values(p.slug).includes(activeSlug)
      ) || null;

    return post;
  }, [activeSlug, lang]);

  // ---------- SEO: title + meta description ----------
  useEffect(() => {
    const baseTitle = "YourSmartCV – Blog";
    const baseDesc =
      "Guides to create ATS-friendly CVs, tailor your applications with AI, and write strong cover letters.";

    let title = baseTitle;
    let description = baseDesc;

    if (activePost) {
      const mt =
        (activePost.metaTitle && activePost.metaTitle[lang]) ||
        (activePost.h1 && activePost.h1[lang]) ||
        baseTitle;
      const md =
        (activePost.metaDescription &&
          (activePost.metaDescription[lang] ||
            activePost.metaDescription.en)) ||
        baseDesc;

      title = `${mt} | YourSmartCV`;
      description = md;
    }

    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, [activePost, lang]);

  // ---------- Handlers for card click / back ----------
  const handleOpenPost = (post) => {
    const slug = post.slug?.[lang] || post.slug?.en;
    if (!slug) return;

    const newPath = `/${lang}/blog/${slug}`;
    window.history.pushState({}, "", newPath);
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToList = () => {
    const newPath = `/${lang}/blog`;
    window.history.pushState({}, "", newPath);
    setActiveSlug(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---------- DETAIL VIEW ----------
  if (activePost) {
    return (
      <section className="mx-auto max-w-3xl px-4 pt-24 pb-16">
        <button
          onClick={handleBackToList}
          className="mb-6 text-sm text-slate-500 hover:text-slate-900"
        >
          ← {tUI(lang, "blog_back") || "Back to blog"}
        </button>

        <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
          {activePost.categoryLabel?.[lang] || "ATS & CV Guides"}
        </p>

        <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 leading-tight mb-4">
          {activePost.h1[lang]}
        </h1>

        <p className="text-sm text-slate-500 mb-8">
          {activePost.readingTime?.[lang] ||
            activePost.excerpt?.[lang] ||
            ""}
        </p>

        <article className="mt-6">
          <ReactMarkdown
            components={{
              // Links inside article body
              a: ({ href, node, ...props }) => (
                <a
                  {...props}
                  href={href}
                  className="text-indigo-700 underline hover:text-indigo-900"
                  onClick={(e) => handleArticleLinkClick(href, e)}
                  target={
                    href?.startsWith("http") || href?.startsWith("https")
                      ? "_blank"
                      : undefined
                  }
                  rel={
                    href?.startsWith("http") || href?.startsWith("https")
                      ? "noopener noreferrer"
                      : undefined
                  }
                />
              ),

              h1: ({ node, ...props }) => (
                <h1
                  className="mt-8 text-[22px] font-semibold leading-7 text-slate-900"
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h2
                  className="mt-8 text-[18px] font-semibold leading-6 text-slate-900"
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h3
                  className="mt-6 text-[16px] font-semibold leading-6 text-slate-900"
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p
                  className="mt-3 text-[14px] leading-6 text-slate-700"
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <ul
                  className="mt-3 list-disc list-inside space-y-1 text-[14px] leading-6 text-slate-700"
                  {...props}
                />
              ),
              li: ({ node, ...props }) => <li {...props} />,
            }}
          >
            {activePost.body?.[lang]}
          </ReactMarkdown>
        </article>
      </section>
    );
  }

  // ---------- LIST VIEW ----------
  return (
    <section className="mx-auto max-w-5xl px-4 pt-24 pb-16">
      <header className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-3">
          {tUI(lang, "blog_title") || "CV & ATS Guides"}
        </h1>
        <p className="text-slate-600 max-w-2xl mx-auto text-sm md:text-base">
          {tUI(lang, "blog_subtitle") ||
            "Practical guides to beat ATS, tailor your CV with AI, and write strong cover letters."}
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        {BLOG_POSTS.map((post) => (
          <button
            key={post.id}
            type="button"
            onClick={() => handleOpenPost(post)}
            className="text-left rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-200"
          >
            <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {post.categoryLabel?.[lang] || "ATS & CV"}
            </p>
            <h2 className="mb-2 text-[17px] font-semibold text-slate-900">
              {post.h1[lang]}
            </h2>
            <p className="mb-3 text-[13px] text-slate-600 line-clamp-3">
              {post.excerpt?.[lang]}
            </p>
            <span className="text-[12px] font-medium text-indigo-900">
              {tUI(lang, "blog_read_more") || "Read guide →"}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
